'use client'

import * as React from 'react'
import { format } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'

import { cn } from './utils'
import { Button } from './Button'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select'
import csLocale from 'date-fns/locale/cs'

interface DatePickerProps {
  date?: Date
  setDate: (date: Date | undefined) => void
  hour?: string
  onHourChange?: (hour: string | undefined) => void
}

export function DatePicker(props: DatePickerProps) {
  const { date, hour, onHourChange } = props
  const showHour = !!onHourChange && !!date

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    props.setDate(undefined)
  }

  const label = React.useMemo(() => {
    if (!date) return <span>Vyberte datum</span>
    // @ts-ignore stupit date-fns types
    const dateStr = format(date, 'PPP', { locale: csLocale })
    return hour ? `${dateStr} do ${hour}:00` : dateStr
  }, [date, hour])

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-empty={!date}
          className="data-[empty=true]:text-muted-foreground justify-between text-left font-normal bg-gray-100">
          <span className="flex-1 text-left">{label}</span>
          {date && (
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0 hover:bg-transparent"
              onClick={handleClear}
              type="button">
              <FontAwesomeIcon icon={faXmark} className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </Button>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar mode="single" selected={date} onSelect={d => props.setDate(d)} />
        {showHour && (
          <div className="border-t px-3 py-2">
            <Select value={hour ?? 'all'} onValueChange={v => onHourChange(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-full">
                <SelectValue placeholder="Hodina" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Celý den</SelectItem>
                {Array.from({ length: 24 }, (_, i) => (
                  <SelectItem key={i} value={String(i).padStart(2, '0')}>
                    do {String(i).padStart(2, '0')}:00
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
